import { useTranslation } from 'react-i18next';

import { VARIANT } from '~/components/isolated/Typography/constants';
import Typography from '~/components/isolated/Typography/Typography';

import { AuthHeader } from '../../components/AuthHeader';
import { OtpInput } from '../../components/OtpInput';
import { OTP_CODE_LENGTH } from '../../constants';

import { OtpFooter } from './components/OtpFooter';
import styles from './OtpView.module.scss';

/**
 * Props for the OtpView component.
 */
interface OtpViewProps {
  /** Phone number to display in the subtitle */
  phoneNumber: string;
  /** Current OTP code value */
  otpCode: string;
  /** Callback fired when OTP value changes */
  onOtpChange: (value: string, isComplete: boolean) => void;
  /** Callback to submit OTP verification */
  onSubmit: () => void;
  /** Callback to go back and change phone number */
  onChangeNumber: () => void;
  /** Callback to close the modal */
  onClose: () => void;
  /** Whether a verification request is in progress */
  isLoading: boolean;
  /** Whether the submit button should be disabled */
  isSubmitDisabled: boolean;
  /** Whether the user account was previously deleted */
  isDeletedUser: boolean;
}

/**
 * OTP verification view for the authentication modal.
 * Displays OTP input with instructions and action buttons.
 */
const OtpView = ({
  phoneNumber,
  otpCode,
  onOtpChange,
  onSubmit,
  onChangeNumber,
  onClose,
  isLoading,
  isSubmitDisabled,
  isDeletedUser,
}: OtpViewProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <AuthHeader
          title={t('phrases:verifyPhoneNumber')}
          onClose={onClose}
          subtitle={
            <Typography
              variant={VARIANT['Text 200/Regular']}
              as="span"
              className={styles.subtitle}
            >
              {t('phrases:enterCode', { count: OTP_CODE_LENGTH })}{' '}
              <Typography
                variant={VARIANT['Text 200/Semibold']}
                as="span"
                className={styles.phoneNumber}
              >
                {phoneNumber}
              </Typography>
            </Typography>
          }
        />

        <OtpInput value={otpCode} onChange={onOtpChange} autoFocus />
      </div>

      <OtpFooter
        onChangeNumber={onChangeNumber}
        onSubmit={onSubmit}
        changeNumberText={t('changeNumber')}
        submitText={
          isDeletedUser ? t('enterOTPAndRecoverAccount') : t('continue')
        }
        isSubmitDisabled={isSubmitDisabled}
        isLoading={isLoading}
      />
    </div>
  );
};

OtpView.displayName = 'OtpView';

export default OtpView;
